import { Link } from '@tanstack/react-router';
import type { CleaningTaskQueueResponse } from '@/shared/api';
import { Button } from '@/shared/components';
import { cn } from '@/shared/lib/cn';
import { cleaningOccupancySummary } from '../cleaningOccupancy';
import {
  nextStep,
  PLAN_PILL as PILL,
  PLAN_TONE,
  planStatus,
  STEP_LABEL,
  type PlanStep,
} from './myTasksModel';
import { arrivalShort, isSameDayArrival, proofLabel, windowLabel } from './taskLabels';

/**
 * Ein Eintrag der Zeitleiste: Apartment, Stand, Fenster, Anreise, Fotonachweis und der eine Knopf.
 * Mit Fotos führt der Knopf in die Aufgabe; ohne Fotos meldet er sie gleich erledigt.
 */
export function PlanEntry({
  task,
  userId,
  today,
  busy,
  locked,
  failure,
  onStep,
}: {
  task: CleaningTaskQueueResponse;
  userId: string | undefined;
  today: string;
  busy: boolean;
  /** Ohne Netz oder während ein anderer Schritt läuft — ein Doppeltipp startet nichts zweimal. */
  locked: boolean;
  failure: string | null;
  onStep: (step: PlanStep) => void;
}) {
  const status = planStatus(task, userId);
  const step = nextStep(task, userId);
  const arrival = arrivalShort(task, today);
  const strong = step === 'finish';
  return (
    <li className="grid grid-cols-[3.25rem_minmax(0,1fr)] gap-2.5">
      <span className="text-ink-soft pt-3.5 font-mono text-xs font-semibold tabular-nums">
        {task.scheduledFromLocalTime.slice(0, 5)}
      </span>
      <div className="border-line border-l-2 pb-3 pl-3 pt-1.5">
        <article
          aria-label={`${task.propertyName}, ${status.label}`}
          className={cn(
            'rounded-tile flex flex-col gap-1.5 bg-white p-3',
            status.tone === 'overdue' ? 'border-sealwax border-2' : 'border-hairline border',
          )}
        >
          <div className="flex items-center gap-2">
            <Link
              to="/cleaning/$id"
              params={{ id: task.id }}
              className="min-h-11 min-w-0 flex-1 content-center truncate font-serif text-xl font-bold hover:underline"
            >
              {task.propertyName}
            </Link>
            <span className={cn(PILL, PLAN_TONE[status.tone])}>{status.label}</span>
          </div>
          <span className="text-ink-soft text-xs">
            {[windowLabel(task), arrival].filter(Boolean).join(' · ')}
          </span>
          {isSameDayArrival(task) && (
            <span className={cn(PILL, 'bg-sealwax-tint text-sealwax self-start')}>
              Anreise am selben Tag · {task.nextArrival?.checkInTime.slice(0, 5)}
            </span>
          )}
          <span className="flex flex-wrap gap-1.5">
            <span
              className={cn(
                PILL,
                task.photoSlotCount ? 'bg-forest-soft text-forest-deep' : 'bg-card-alt text-ink',
              )}
            >
              {proofLabel(task)}
            </span>
            {task.timeZoneDataQualityCode && (
              <span className={cn(PILL, 'bg-amber-tint text-amber-ink')}>Zeitzone prüfen</span>
            )}
          </span>
          {task.nextArrival && (
            <span className="text-ink-faint text-xs">
              {cleaningOccupancySummary(task.nextArrival)}
            </span>
          )}
          {step && (
            <Button
              variant={strong ? 'brand' : 'secondary'}
              size="lg"
              className={cn('mt-1 w-full', !strong && 'border-forest text-forest border bg-white')}
              loading={busy}
              disabled={locked}
              onClick={() => onStep(step)}
            >
              {STEP_LABEL[step]}
            </Button>
          )}
          {failure && (
            <p role="alert" className="text-sealwax text-sm font-semibold">
              {failure}
            </p>
          )}
        </article>
      </div>
    </li>
  );
}
